import React from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SecurityIcon from '@mui/icons-material/Security';
import RestoreIcon from '@mui/icons-material/Restore';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import BuildIcon from '@mui/icons-material/Build';
import SchoolIcon from '@mui/icons-material/School';

const Home = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <SecurityIcon sx={{ fontSize: 60, color: '#0d3b66' }} />,
      title: 'PII Breach Monitor',
      description: 'Check if your personal information has been compromised in data breaches.',
      action: 'Check Now',
      path: '/breach-check'
    },
    {
      icon: <RestoreIcon sx={{ fontSize: 60, color: '#0d3b66' }} />,
      title: 'Identity Recovery',
      description: 'Step-by-step guidance to recover from identity theft and protect your future.',
      action: 'Get Started',
      path: '/identity-recovery'
    },
    {
      icon: <NewspaperIcon sx={{ fontSize: 60, color: '#0d3b66' }} />,
      title: 'Security News',
      description: 'Stay informed with daily curated cybersecurity and technology news.',
      action: 'Read Articles',
      path: '/news'
    }
  ];

  const services = [
    {
      icon: <DeveloperBoardIcon sx={{ fontSize: 48, color: '#d4af37' }} />,
      title: 'Software Solutions',
      description: 'Customized software tailored to your unique needs with ongoing support.',
    },
    {
      icon: <BuildIcon sx={{ fontSize: 48, color: '#d4af37' }} />,
      title: 'Hardware Support',
      description: 'Expert assistance with computers, phones, and all your hardware needs.',
    },
    {
      icon: <SchoolIcon sx={{ fontSize: 48, color: '#d4af37' }} />,
      title: 'Training & Education',
      description: 'Empower yourself or your team with technology training and guidance.',
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0d3b66 0%, #1a5490 100%)',
          color: 'white',
          py: 10,
          mb: 6
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
              <Typography variant="h2" component="h1" fontWeight="700" gutterBottom>
                We work hard, so you can live Smart!
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
                Protecting your digital identity and integrating technology seamlessly into your life.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/breach-check')}
                  sx={{
                    backgroundColor: '#d4af37',
                    '&:hover': { backgroundColor: '#b8941f' },
                    fontWeight: 600,
                    px: 4,
                    py: 1.5
                  }}
                >
                  Check for Breaches
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate('/about')}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': { borderColor: '#d4af37', backgroundColor: 'rgba(212, 175, 55, 0.1)' },
                    fontWeight: 600,
                    px: 4,
                    py: 1.5
                  }}
                >
                  Learn More
                </Button>
              </Box>
          </Box>
        </Container>
      </Box>

      {/* Security Tools Section */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Typography variant="h3" component="h2" align="center" fontWeight="700" gutterBottom>
          Security & Privacy Tools
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
          Comprehensive tools to protect and monitor your digital identity
        </Typography>
        
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'transform 0.3s, box-shadow 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: 8
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 4 }}>
                  <Box sx={{ mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h5" component="h3" fontWeight="600" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    {feature.description}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate(feature.path)}
                    sx={{
                      backgroundColor: '#0d3b66',
                      '&:hover': { backgroundColor: '#1a5490' },
                      fontWeight: 600
                    }}
                  >
                    {feature.action}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Services Section */}
      <Box sx={{ backgroundColor: '#f8f9fa', py: 8, mb: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" align="center" fontWeight="700" gutterBottom>
            Technology Services
          </Typography>
          <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
            Expert solutions for all your technology needs
          </Typography>
          
          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 4,
                    height: '100%',
                    textAlign: 'center',
                    transition: 'box-shadow 0.3s',
                    '&:hover': { boxShadow: 6 }
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    {service.icon}
                  </Box>
                  <Typography variant="h6" component="h3" fontWeight="600" gutterBottom>
                    {service.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {service.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/services')}
              sx={{
                backgroundColor: '#d4af37',
                '&:hover': { backgroundColor: '#b8941f' },
                fontWeight: 600,
                px: 6,
                py: 1.5
              }}
            >
              View All Services
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Mission Statement */}
      <Container maxWidth="md" sx={{ mb: 8, textAlign: 'center' }}>
        <Paper elevation={3} sx={{ p: 6, borderTop: '4px solid #d4af37' }}>
          <Typography variant="h4" component="h2" fontWeight="700" gutterBottom>
            Our Mission
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            Our goal is to help you integrate technology seamlessly into your life, prioritizing comfort 
            and practicality. We strongly believe that technological advancements have the power to improve 
            your quality of life when adopted thoughtfully.
          </Typography>
        </Paper>
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          backgroundColor: '#0d3b66',
          color: 'white',
          py: 8,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" fontWeight="700" gutterBottom>
            Ready to Get Started?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            Contact us today for a consultation
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/about')}
              sx={{
                backgroundColor: '#d4af37',
                '&:hover': { backgroundColor: '#b8941f' },
                fontWeight: 600,
                px: 6,
                py: 1.5
              }}
            >
              Contact Us
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => window.location.href = 'tel:561-344-4801'}
              sx={{
                borderColor: 'white',
                color: 'white',
                '&:hover': { borderColor: '#d4af37', backgroundColor: 'rgba(212, 175, 55, 0.1)' },
                fontWeight: 600,
                px: 6,
                py: 1.5
              }}
            >
              Call: 561-344-4801
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
